import { request, getAuth } from "@sharpana/auth/api_client/allauth";
import { UserActivityType, ResourceType } from "../types/activity_types";

/**
 * Store user activity for recommendation interactions
 * This function will toggle (add/remove) bookmarks and likes
 * @param document_id The ID of the document
 * @param resource_type The type of resource
 * @param activity_type The type of activity
 * @param derived_from_resource_id The ID of the underlying resource (only used when resource_type is "yt_sharp")
 * @returns An object with success status and activity_id if successful
 */
export async function storeUserActivity(
  document_id: string,
  resource_type: ResourceType,
  activity_type: UserActivityType,
  derived_from_resource_id: string = ""
): Promise<{ success: boolean; activity_id?: string; soft_deleted?: boolean }> {
  return await request("POST", "/api/store-user-activity", {
    resource_id: document_id, // Backend still expects 'resource_id'
    resource_type: resource_type,
    activity_type: activity_type,
    derived_from_resource_id: derived_from_resource_id,
  });
}

/**
 * Add a channel to the user's hidden list
 * @param ytChannelId The YouTube channel ID to hide
 */
export async function addChannelToHiddenList(ytChannelId: string) {
  return await request("POST", "/api/add-channel-to-hidden-list", {
    yt_channel_id: ytChannelId,
  });
}

/**
 * Update user's preferred content categories during onboarding
 * @param categories List of content categories selected by the user
 * @returns An object with success status and categories if successful
 */
export async function updateUserCategories(categories: string[]) {
  // Update categories on the server
  const response = await request("POST", "/api/update-user-categories", {
    categories,
  });

  // If update was successful, refresh the user session
  if (response.success) {
    // This will update the auth context with the latest user data
    await getAuth();
  }

  return response;
}

/**
 * Progress information for the onboarding item ranking process
 */
export interface OnboardingProgress {
  current_sample_number: number;
  normal_total_samples: number;
  onboarding_state: 'in_progress' | 'extra_sample' | 'complete';
}

/**
 * Response from the onboarding item endpoint
 */
export interface OnboardingItemResponse {
  success: boolean;
  activity_id?: string;
  next_item?: any;  // This will be a RecommendationItem
  progress?: OnboardingProgress;
  error?: string;
}

/**
 * Get or rate an onboarding item
 * @param options Optional parameters for rating an item
 * @returns An object with success status, next item, and progress information
 */
export async function getOrRateOnboardingItem(options?: {
  resource_id: string;
  resource_type: ResourceType;
  activity_type: UserActivityType;
}): Promise<OnboardingItemResponse> {
  // Always use the unified endpoint with POST
  if (!options) {
    // If no parameters are provided, just get the next item without rating
    return await request("POST", "/api/onboarding/item");
  }
  
  // Otherwise, rate the current item and get the next one
  return await request("POST", "/api/onboarding/item", {
    resource_id: options.resource_id,
    resource_type: options.resource_type,
    activity_type: options.activity_type,
    derived_from_resource_id: "", // Onboarding items are only full yt videos at this point, so no need to specify this as it's the case for ytsharps
  });
}

// Backward compatibility functions have been removed in favor of the unified getOrRateOnboardingItem function
